import React, { useState } from 'react';
import {
  FileSpreadsheet,
  Download,
  FileText,
  File,
  CheckCircle2,
  AlertTriangle,
  Layers,
  HelpCircle,
  Ban,
  CornerUpLeft,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { useReconciliation } from '../../context/ReconciliationContext';
import { ReportType, ExportFormat } from '../../types/reconciliation';
import { triggerExport, filterItemsForReport } from '../../engine/exporter';
import { useAuth } from '../../context/AuthContext';

export const ReportsHub: React.FC = () => {
  const { items, summary } = useReconciliation();
  const { logAction } = useAuth();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  if (!summary) {
    return (
      <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm text-center">
        <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">
          No Active Reconciliation Session
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
          Please run reconciliation or load the demo dataset from the Upload section to generate reports.
        </p>
      </div>
    );
  }

  const reportsConfig: {
    id: ReportType;
    title: string;
    description: string;
    icon: React.FC<{ className?: string }>;
    color: string;
    bg: string;
    itemCount: number;
    amountMetric: string;
  }[] = [
    {
      id: 'combined_master',
      title: '7. Combined Master Report',
      description: 'Comprehensive audit ledger of all reconciled transactions, split ERP bills, variances, and refunds.',
      icon: FileSpreadsheet,
      color: 'text-brand-600 dark:text-brand-400',
      bg: 'bg-brand-50/70 dark:bg-brand-950/40 border-brand-200 dark:border-brand-800',
      itemCount: items.length,
      amountMetric: `Collections: ₹${summary.totalCollectionAmount.toLocaleString('en-IN')}`,
    },
    {
      id: 'exact_match',
      title: '1. Exact Match Report',
      description: 'Zero-variance transactions where Razorpay payments equal ERP total multi-bill sums. Clearance ready.',
      icon: CheckCircle2,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-50/70 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800',
      itemCount: summary.exactMatches,
      amountMetric: `Reconciled: ₹${summary.totalReconciledAmount.toLocaleString('en-IN')}`,
    },
    {
      id: 'partial_match',
      title: '2. Partial Match (Under-Billed) Report',
      description: 'Customer payment exceeds ERP bills. Indicates pending installment bills or unposted invoices.',
      icon: AlertTriangle,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-50/70 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      itemCount: summary.partialMatches,
      amountMetric: 'Action Required',
    },
    {
      id: 'over_billed',
      title: '3. Over Billed Exception Report',
      description: 'ERP consolidated bills exceed Razorpay payment. Highlights duplicate billing or multi-payment splits.',
      icon: Layers,
      color: 'text-purple-600 dark:text-purple-400',
      bg: 'bg-purple-50/70 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800',
      itemCount: summary.overBilled,
      amountMetric: 'Variance Risk',
    },
    {
      id: 'no_bill_reference',
      title: '4. Missing ERP Bill Reference Report',
      description: 'Payment captured in Razorpay gateway but completely absent in ERP accounts ledger.',
      icon: HelpCircle,
      color: 'text-rose-600 dark:text-rose-400',
      bg: 'bg-rose-50/70 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800',
      itemCount: summary.noBillReference,
      amountMetric: 'Missing Vouchers',
    },
    {
      id: 'erp_only',
      title: '5. ERP Only (Uncollected Bills) Report',
      description: 'Bills posted in ERP with Payment ID reference, but zero gateway payment received.',
      icon: Ban,
      color: 'text-indigo-600 dark:text-indigo-400',
      bg: 'bg-indigo-50/70 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800',
      itemCount: summary.erpOnly,
      amountMetric: 'Uncollected',
    },
    {
      id: 'refund',
      title: '6. Refund Audit Report',
      description: 'All transactions marked as refunded in Razorpay. Requires credit note verification in ERP.',
      icon: CornerUpLeft,
      color: 'text-sky-600 dark:text-sky-400',
      bg: 'bg-sky-50/70 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800',
      itemCount: summary.refundedCount,
      amountMetric: 'Reversals',
    },
    {
      id: 'daily_summary',
      title: '8. Daily Summary & KPI Report',
      description: 'Executive briefing containing batch KPIs, ledger concordance scores, and risk analysis metrics.',
      icon: Calendar,
      color: 'text-teal-600 dark:text-teal-400',
      bg: 'bg-teal-50/70 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800',
      itemCount: items.length,
      amountMetric: `${summary.matchPercentage}% Match Rate`,
    },
  ];

  const handleExport = (reportType: ReportType, format: ExportFormat) => {
    const key = `${reportType}_${format}`;
    setDownloadingId(key);

    try {
      triggerExport(items, summary, reportType, format);
      logAction('REPORT_EXPORT', `Exported ${reportType.toUpperCase()} report in ${format.toUpperCase()} format.`);
    } catch (err) {
      console.error('Export error:', err);
      alert('Error generating export file.');
    } finally {
      setTimeout(() => setDownloadingId(null), 1000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Enterprise Reports & Exports Hub
            </h2>
            <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 font-semibold border border-emerald-200 dark:border-emerald-800">
              8 Formatted Reports
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Export ready-to-audit reports in formatted Microsoft Excel (XLSX), Comma-Separated Values (CSV), or Landscape Corporate PDF.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('combined_master', 'xlsx')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition"
          >
            <Download className="w-4 h-4" />
            <span>Master Excel</span>
          </button>
          <button
            onClick={() => handleExport('combined_master', 'pdf')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 text-white text-xs font-semibold shadow-sm transition"
          >
            <Download className="w-4 h-4" />
            <span>Master PDF</span>
          </button>
        </div>
      </div>

      {/* Grid of 8 Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reportsConfig.map((rep) => {
          const Icon = rep.icon;

          return (
            <div
              key={rep.id}
              className={`p-5 rounded-2xl border ${rep.bg} flex flex-col justify-between transition hover:shadow-md`}
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl bg-white dark:bg-slate-800 shadow-xs ${rep.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {rep.title}
                      </h4>
                      <span className="text-[11px] font-mono font-semibold text-slate-600 dark:text-slate-300">
                        {rep.itemCount} records &bull; {rep.amountMetric}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed my-3">
                  {rep.description}
                </p>
              </div>

              {/* Download buttons for XLSX, CSV, PDF */}
              <div className="pt-3 border-t border-slate-200/60 dark:border-slate-800 flex items-center gap-2">
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mr-1">
                  Export:
                </span>

                <button
                  onClick={() => handleExport(rep.id, 'xlsx')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:border-emerald-500 text-slate-800 dark:text-slate-200 text-xs font-medium shadow-xs transition"
                  title="Download Microsoft Excel format (.xlsx)"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                  <span>XLSX</span>
                </button>

                <button
                  onClick={() => handleExport(rep.id, 'csv')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:border-brand-500 text-slate-800 dark:text-slate-200 text-xs font-medium shadow-xs transition"
                  title="Download CSV format (.csv)"
                >
                  <FileText className="w-3.5 h-3.5 text-brand-600" />
                  <span>CSV</span>
                </button>

                <button
                  onClick={() => handleExport(rep.id, 'pdf')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:border-rose-500 text-slate-800 dark:text-slate-200 text-xs font-medium shadow-xs transition"
                  title="Download Corporate Landscape PDF (.pdf)"
                >
                  <File className="w-3.5 h-3.5 text-rose-600" />
                  <span>PDF</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
